import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils.js';

export interface ModalProps {
  /** Controls whether the modal is visible. */
  open: boolean;
  /** Called whenever modal open state changes. */
  onOpenChange: (open: boolean) => void;
  /** Title displayed in the modal header. */
  title: string;
  /** Optional description displayed under the title. */
  description?: string;
  /** Modal body content. */
  children: ReactNode;
  /** Footer actions rendered at the bottom of the modal. */
  footer?: ReactNode;
  /** Width preset for desktop dialog layout. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** On mobile, render as full-height bottom sheet style. */
  mobileFull?: boolean;
  /** Extra classes for modal panel. */
  className?: string;
}

const sizeClasses: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'md:max-w-md',
  md: 'md:max-w-lg',
  lg: 'md:max-w-2xl',
  xl: 'md:max-w-4xl',
};

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  mobileFull = true,
  className,
}: ModalProps) {
  const { t } = useTranslation();

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-[var(--overlay)] backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0'
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed z-50 flex w-full flex-col border border-[var(--border-default)] bg-[var(--bg-elevated)] text-[var(--text-primary)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ig-focus-ring-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ig-focus-ring-offset-color)]',
            mobileFull
              ? 'inset-x-0 bottom-0 max-h-[90vh] rounded-t-sheet p-5 data-[state=open]:slide-in-from-bottom-6 data-[state=closed]:slide-out-to-bottom-6 md:inset-auto md:left-1/2 md:top-1/2 md:max-h-[85vh] md:w-[calc(100%-2rem)] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg'
              : 'left-1/2 top-1/2 max-h-[85vh] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-lg p-5',
            sizeClasses[size],
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            className
          )}
        >
          {mobileFull ? (
            <div className="mb-3 flex justify-center md:hidden">
              <span className="h-1.5 w-12 rounded-full bg-[var(--border-strong)]" aria-hidden />
            </div>
          ) : null}
          <div className="mb-4 flex items-start justify-between gap-3">
            <div className="space-y-1">
              <Dialog.Title className="font-display text-head-24 text-[var(--text-primary)]">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="text-body-14 text-[var(--text-secondary)]">{description}</Dialog.Description>
              ) : null}
            </div>
            <Dialog.Close
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[var(--border-default)] text-[var(--text-secondary)] transition hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ig-focus-ring-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ig-focus-ring-offset-color)]"
              aria-label={t('common.modal.closeDialog', { defaultValue: 'Close dialog' })}
            >
              <X size={20} aria-hidden />
            </Dialog.Close>
          </div>
          {/* min-h-0 lets the flex body actually shrink so long content
              scrolls here instead of overflowing the max-h panel. */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain text-body-14">{children}</div>
          {footer ? (
            <div className="mt-5 shrink-0 border-t border-[var(--border-default)] pt-4">
              {footer}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
